import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/contexts/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { CircleNotch } from "@phosphor-icons/react";
import { Logo } from "@/components/Logo";

export default function Register() {
    const { register } = useAuth();
    const { t } = useT();
    const nav = useNavigate();
    const [form, setForm] = useState({ name: "", email: "", password: "" });
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        const r = await register(form.email, form.password, form.name);
        setLoading(false);
        if (r.ok) {
            toast.success(t("welcome"));
            nav("/", { replace: true });
        } else {
            toast.error(r.error);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-6 bg-background">
            <Card className="w-full max-w-md p-8 card-paper shadow-none" data-testid="register-card">
                <div className="mb-6">
                    <Logo size={30} primary="#1A3626" accent="#D99533" subtitle={t("tagline")} subtitleClass="text-[#5C6B61]" />
                </div>
                <h2 className="font-display text-3xl font-semibold tracking-tight mb-1">{t("register_title")}</h2>
                <p className="text-sm text-muted-foreground mb-6">{t("register_subtitle")}</p>
                <form onSubmit={submit} className="space-y-4">
                    <div className="space-y-1.5">
                        <Label htmlFor="name">{t("name")}</Label>
                        <Input
                            id="name"
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            required
                            data-testid="register-name-input"
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="email">{t("email")}</Label>
                        <Input
                            id="email"
                            type="email"
                            value={form.email}
                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                            required
                            data-testid="register-email-input"
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="password">{t("password")}</Label>
                        <Input
                            id="password"
                            type="password"
                            value={form.password}
                            onChange={(e) => setForm({ ...form, password: e.target.value })}
                            required
                            minLength={6}
                            data-testid="register-password-input"
                        />
                    </div>
                    <Button type="submit" disabled={loading} className="w-full h-11 bg-[#1A3626] hover:bg-[#132A1D] text-white" data-testid="register-submit-btn">
                        {loading ? <CircleNotch className="animate-spin mr-2" size={16} /> : null}
                        {t("create_account")}
                    </Button>
                </form>
                <div className="mt-6 text-sm text-muted-foreground text-center">
                    {t("have_account")}{" "}
                    <Link to="/login" className="text-[#1A3626] font-medium hover:underline" data-testid="go-login">
                        {t("login")}
                    </Link>
                </div>
            </Card>
        </div>
    );
}
